/** The bottom-right sheet: the selected hero, and whatever the pointer is over.
 *
 * Two blocks, always in the same order, because a panel whose contents jump
 * around is a panel you stop reading. The hero block is the tall one; the hover
 * block is a fixed three lines at the bottom so the hero block never reflows
 * when the pointer crosses the map.
 */

import { useMemo, type CSSProperties } from "react";
import type {
  ArmySlot, ArmyState, ContentRegistry, CreatureDefId, HeroDefId, HeroId,
  ObservedHero, SkillDefId,
} from "@game/core";
import {
  BitmapText, fitBitmapText, fitRomanStep, measureDrawnBitmapText, romanFaceForStep, useBitmapScale,
  useDevicePixelRatio,
} from "../theme/bitmapText.js";
import { Frame, Icon, Panel, ScrollArea, Slot, cx, useElementSize } from "../theme/components.js";
import {
  PRIMARY_ICON, PRIMARY_LABEL, PRIMARY_ORDER, creatureFrameKey, portraitKey, uiIconKey,
} from "../theme/art.js";
import { METRICS, playerColour } from "../theme/tokens.js";
import { useContent, useObservation } from "../hooks/useGameState.js";
import {
  formatCount, formatNumber, gaugeReading, humanise, skillLevelName,
} from "./format.js";
import { Gauge, gaugeStackScale } from "./HeroList.js";
import { findHero } from "./select.js";
import { heroDisplayName } from "./hero-name.js";
import type { HeroCapacities } from "./types.js";

export interface InfoPanelProps {
  readonly selectedHeroId: HeroId | null;
  /** Movement / mana ceilings from the host; the same ones the rack's gauges
   *  use, so the sheet and the rack can never disagree. */
  readonly capacities?: HeroCapacities | undefined;
  readonly className?: string | undefined;
}

export function InfoPanel(props: InfoPanelProps): JSX.Element {
  const obs = useObservation();
  const content = useContent();
  const hero = useMemo(() => findHero(obs, props.selectedHeroId), [obs, props.selectedHeroId]);
  // The column is 174 design pixels and the kit's scale steps make that 174 CSS
  // px at 1366x768 and 261 at 1080p. An `Icon` takes a number, not a token, so
  // the sheet measures itself: a portrait pinned at one size either wastes half
  // the row on a big screen or eats the hero's name on a small one.
  const { ref: boxRef, width } = useElementSize<HTMLDivElement>();

  return (
    <Panel className={cx("hm-col", props.className)} style={{ padding: "var(--hm-pad)", minHeight: 0 }}>
      {/* The army rack is a SIBLING of the scroll, not its last child.
       *
       * The sidebar's vertical budget is 690 usable pixels at 1366x768 and the
       * sheet's share of it is 364. A day-one hero fits; a real one does not.
       * Measured in this build at 1366x768, giving the selected hero the game's
       * maximum of eight secondary skills grows the sheet's content by 60px and
       * pushes the army rack 53px BELOW the panel's clip - seven sockets, two
       * creatures and both count plaques gone from the screen with nothing to
       * say they exist. At 1920x1080 the same hero loses it by 79px, so this is
       * not a laptop-only fault; 1366 is only where it bites first.
       *
       * So the decision this panel has to make - what gives when the column is
       * short - is made here, once, and it is the SKILL LIST that gives. A
       * secondary skill is a reference: its name and mastery are also on the
       * hero screen, and scrolling two rows to reach one costs nothing. An army
       * is the number the player checks before every move, it exists nowhere
       * else on this screen, and a rack that has silently scrolled out from
       * under the fold is indistinguishable from a hero with no troops.
       *
       * Nothing here shrinks a bitmap numeral to buy the room: the count
       * plaques step by whole magnifications and stay at the one they are on.
       */}
      <div ref={boxRef} className="hm-col hm-grow" style={{ minHeight: 0 }}>
        <ScrollArea className="hm-grow" style={{ minHeight: 0 }}>
          <div style={{ width: "100%", minHeight: "100%", display: "flex" }}>
            {hero === null
              ? <EmptySheet />
              : (
                <HeroSheet
                  hero={hero}
                  content={content}
                  colour={playerColour(obs.self?.colour ?? null)}
                  capacity={props.capacities?.[hero.id as string] ?? null}
                  width={width}
                />
              )}
          </div>
        </ScrollArea>
        {hero !== null && (
          <ArmyStrip army={hero.army} content={content} width={width} />
        )}
      </div>
    </Panel>
  );
}

function EmptySheet(): JSX.Element {
  return (
    <div className="hm-grow hm-col" style={{ justifyContent: "center", alignItems: "center" }}>
      <span className="hm-label">Select a hero.</span>
    </div>
  );
}

// ----------------------------------------------------------------- hero sheet

function HeroSheet(props: {
  readonly hero: ObservedHero;
  readonly content: ContentRegistry | null;
  readonly colour: string;
  readonly capacity: { readonly movementMax: number; readonly manaMax: number } | null;
  /** Measured inner width of the sheet, in CSS pixels. */
  readonly width: number;
}): JSX.Element {
  const { hero, content } = props;
  const detail = hero.detail;
  const primary = hero.primary ?? detail?.primary ?? null;
  const portrait = useMemo(() => {
    const def = content?.heroes.get(hero.def as HeroDefId);
    return def === undefined ? null : portraitKey(def.portrait);
  }, [content, hero.def]);

  // Where this hero sits in the level band, so the sheet can show progress to
  // the next level rather than a bare experience count that means nothing on
  // its own. The thresholds are content, not a rule the panel invents.
  const thresholds = props.content?.levels.thresholds ?? [];
  const here = thresholds[Math.max(0, props.hero.level - 1)] ?? 0;
  const next = thresholds[props.hero.level] ?? null;

  // Two fifths of the column for the face, so the name and class keep the other
  // three and never fall to "A...".
  const portraitPx = props.width <= 0
    ? METRICS.portraitMedium
    : Math.max(48, Math.min(128, Math.round(props.width * 0.44)));

  // Four pills share the sidebar's width, and at 1366 that sidebar is 100px
  // narrower with the same four readings in it, so the figure steps down a
  // whole magnification rather than being squeezed onto a half pixel.
  const statScale = useBitmapScale(2);
  // The mark steps with the reading beside it, on the same media step, because
  // the two share a 38px pill at 1366x768 and a 56px one at 1920x1080 and they
  // are competing for the same run of pixels. At magnification 2 a two-digit
  // primary is 22px wide and the medium icon leaves 23; at magnification 1 it
  // is 11px and the small icon leaves 16. Either way the reading clears the
  // pill's right lip, which a 24px icon at 1366 did not - it left 8.
  const statIconPx = statScale >= 2 ? METRICS.iconMedium : METRICS.iconSmall;
  // Both ceilings are the kernel's (`dailyMovement`, `maxMana`), handed over
  // by the host in `capacities`. No fallback: the panel used to print
  // knowledge x 10 when the host had not wired it, and that is wrong for
  // every hero whose artifacts or skills touch the pool, because the kernel's
  // `maxMana` also resolves a "mana" modifier the panel cannot see. Without
  // the figure the gauge shows the reading alone and claims no ceiling.
  const movementMax = props.capacity?.movementMax ?? null;
  const manaMax = props.capacity?.manaMax ?? null;
  const reading = (value: number, max: number | null): string =>
    max === null ? formatNumber(value) : gaugeReading(value, max);
  const xpDisplay = detail === null || next === null
    ? ""
    : `${formatNumber(detail.experience)} / ${formatNumber(next)}`;

  // ONE magnification for all three gauges, picked from the widest of them.
  //
  // Each gauge measuring only itself is not the same rule and it looked like a
  // mistake: on the 1920x1080 re-shot the Experience and Mana plates took
  // magnification 2 and the Movement plate between them - `1,600 / 1,600`, one
  // reading wider - took 1, so a 90px band of three identical plates carried
  // two sizes of one face. A stack picks a step; a plate does not.
  const dpr = useDevicePixelRatio();
  const gaugeScale = gaugeStackScale(props.width, [
    ["Experience", xpDisplay],
    ["Movement", reading(detail?.movementLeft ?? 0, movementMax)],
    ["Mana", reading(detail?.mana ?? 0, manaMax)],
  ], dpr);

  // And one for the skill rail, picked the same way and for the same reason:
  // `Warding` is half again as wide as `Wisdom` and a per-row step would
  // set eight plates at two sizes. `SKILL_CHROME_PX` is the medallion, its
  // socket, the mastery plate and the air between them at the widest scale
  // step the kit ships.
  const skillScale = useMemo(() => {
    // Measured as drawn (floor applied, under the ratio the page is at), so
    // the step picked is the step that fits; see `fitRomanStep`.
    const rows = (detail?.secondary ?? []).map((s) =>
      `${props.content?.skills.get(s.def)?.name ?? humanise(s.def as string)} ${skillLevelName(s.level)}`);
    return fitRomanStep(props.width - SKILL_CHROME_PX, rows, dpr);
  }, [detail, props.content, props.width, dpr]);

  // The wording the name column holds. A bitmap caption cannot be squeezed
  // - the floor holds its face and magnification while the column is a fifth
  // narrower on the glass under an 80% page - so the fullest wording that
  // fits is stamped and the rest is dropped whole, never cut to `Knigh`. The
  // room is the sheet less the face beside it and the row's gutter.
  const heroClass = content?.heroes.get(hero.def as HeroDefId)?.heroClass ?? "";
  const level = `Level ${String(hero.level)}`;
  const stateLine = fitBitmapText(
    props.width - portraitPx - NAME_COLUMN_CHROME_PX,
    // No hyphenated wording: the classic writes `Level 5 Shadowcaller`, and a caption
    // that read `Level 1 - Knight` at 100% and `Level 1 Knight` at 80% was
    // copy that changed with the zoom.
    heroClass === "" ? [level] : [`${level} ${heroClass}`, level],
    1, "roman", dpr, true,
  );
  // The class is never dropped: the classic prints `Level N Class` whatever the
  // room, so when the row cannot hold `Level 1 Knight` (a 1366x768 window
  // at 80% leaves 95 device px for 98) the class takes a second line.
  const classLine = stateLine === level && heroClass !== "" ? heroClass : null;

  return (
    <div
      className="hm-col hm-grow"
      // The army rack is no longer in this column - it is pinned to the panel's
      // foot by `InfoPanel` - so what is distributed here is only the four
      // reading groups, and the slack is spread between them rather than
      // pooled.
      //
      // That is the opposite of what an older note here claimed, and the note
      // was wrong for a measured reason. At 1366x768 with a day-one hero the
      // sheet's content is 92px shorter than its 364px share of the column,
      // and that 92px has to go somewhere. Pooled at the foot it is one 90px
      // slab of bare wood directly above the rack, which reads as a panel that
      // failed to draw; spread, it is four 23px bands, which read as the air
      // between groups. A real hero closes both: eight skills and a full army
      // leave no slack at all and this rule stops mattering.
      // Half a gutter between groups: the marks are the window's cap floor
      // tall (15 rows at 1920x1080), and at a whole gutter the four groups
      // ran 31px past the column there. The groups sit at that fixed pitch
      // and the slack pools at the foot, the way the classic packs its hero box:
      // spread by `space-between`, a 1920x1080 window at 80% opened a
      // 50-row band between Experience and Movement.
      style={{ minHeight: "100%", gap: "calc(var(--hm-gutter) / 2)" }}
    >
      {/* Stretch, not flex-start. The name column is the shorter of the two and
          it carries the primaries at its foot, so it has to be as tall as the
          face beside it for `margin-top: auto` to have anything to push
          against. The portrait opts out with its own `align-self`: it is a
          fixed square of art and a stretched mount would hang below it. */}
      <div className="hm-row" style={{ alignItems: "stretch" }}>
        {/* The mount is drawn over the art's own outermost pixels rather than
            padded around it: the portrait snaps to a whole divisor of its 256px
            source, so 85 is 256/3 and the next size down is 64, and buying a
            3px margin would cost a quarter of the face. See `.hm-adv-portrait`. */}
        <Frame
          well
          className="hm-adv-portrait hm-adv-portrait--large"
          style={{ padding: 0, lineHeight: 0, flex: "0 0 auto", alignSelf: "flex-start" }}
        >
          {/* 256px source; `fitSprite` snaps to a whole divisor of it. */}
          {portrait === null
            ? <span className="hm-icon hm-icon--missing" style={{ width: portraitPx, height: portraitPx }} />
            : <Icon atlasKey={portrait} size={portraitPx} />}
        </Frame>

        <div className="hm-col hm-grow" style={{ gap: "calc(var(--hm-u) * 2)", minWidth: 0 }}>
          {/* Cut into a strip, the way the classic cuts it: it is what binds the
              portrait, the name and the primaries into one card instead of
              three neighbours. It spends no height - the name column beside an
              85px face had that air already. */}
          <span className="hm-adv-heroname">
            {/* Stamped, not typeset. It is the card's title and it sits directly
                over four bitmap stat readings and three bitmap gauges; in the
                kit's serif it was the one string on the card set in another
                face. The face has a real lowercase now, so this does not have
                to become small caps to be stamped. */}
            <BitmapText face={romanFaceForStep(statScale)} scale={1}>{heroDisplayName(hero, content)}</BitmapText>
          </span>
          {/* Stamped, not typeset. `Elifell` above it is a bitmap mark and
              this line sat nine pixels under it in an anti-aliased serif - the
              two type systems a reviewer measured twenty pixels apart on one
              plate. */}
          <span className="hm-adv-roster__state hm-clip">
            <BitmapText face="roman" scale={1} shadow>
              {stateLine}
            </BitmapText>
          </span>
          {classLine === null ? null : (
            <span className="hm-adv-roster__state hm-clip">
              <BitmapText face="roman" scale={1} shadow>
                {classLine}
              </BitmapText>
            </span>
          )}
          {detail !== null && next === null && (
            <span className="hm-num" style={{ fontSize: "var(--hm-fs-tiny)", opacity: 0.85 }}>
              {formatNumber(detail.experience)} xp
            </span>
          )}

          {/* The four primaries, two by two, at the foot of the name column.
              They were a full-width band under this row and the band beside the
              face was empty; see `.hm-adv-stats`. */}
          {primary !== null && (
            <div className="hm-adv-stats">
              {PRIMARY_ORDER.map((key) => (
                <div key={key} className="hm-adv-stat" title={PRIMARY_LABEL[key]}>
                  {/* `hm-relief__inlay` screens the mark onto the pill's own
                      floor. Every `ui.*` frame in the shipped atlas is authored
                      full-bleed on an opaque black ground, so unblended each
                      primary was a 24x24 block of exactly (0,0,0) inside a lit
                      socket - measured on the shipped 1080p shot, 387 / 306 /
                      404 / 333 px of pure black across the four pills. */}
                  <Icon
                    atlasKey={PRIMARY_ICON[key]}
                    size={statIconPx}
                    title={PRIMARY_LABEL[key]}
                    className="hm-relief__inlay"
                  />
                  <span className="hm-adv-stat__value">
                    <BitmapText face={romanFaceForStep(statScale)} scale={1}>{primary[key]}</BitmapText>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Full width, under the portrait row: a gauge squeezed into the name
          column is narrower than its own caption and drops its reading. */}
      {detail !== null && next !== null && (
        <Gauge
          kind="xp"
          label="Experience"
          value={detail.experience - here}
          max={Math.max(1, next - here)}
          display={xpDisplay}
          scale={gaugeScale}
        />
      )}

      {/* The two numbers a player checks every turn belong on the sheet, not
          only on the rack row above and as text on the plate below. They also
          fill the band that used to be bare wood between the stats and the
          skills. */}
      {detail !== null && (
        <div className="hm-col" style={{ gap: "calc(var(--hm-u) * 2)" }}>
          <Gauge kind="move" label="Movement" value={detail.movementLeft}
            max={movementMax ?? detail.movementLeft}
            display={movementMax === null ? formatNumber(detail.movementLeft) : undefined}
            scale={gaugeScale} />
          <Gauge kind="mana" label="Mana" value={detail.mana}
            max={manaMax ?? detail.mana}
            display={manaMax === null ? formatNumber(detail.mana) : undefined}
            scale={gaugeScale} />
        </div>
      )}

      {/* The rail the column gives up first when the window is short - see
          `.hm-adv-skillrail`. Every skill is on the hero sheet, and the classic's
          own sidebar carries no secondary-skill list at all. */}
      {detail !== null && detail.secondary.length > 0 && (
        <div className="hm-col hm-adv-skillrail" style={{ gap: 0 }}>
          {detail.secondary.map((s) => (
            <SkillLine
              key={s.def}
              content={content}
              def={s.def}
              level={s.level}
              scale={skillScale}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * What a skill row spends on anything that is not a glyph, at the widest scale
 * step the kit ships: the 16px medallion, its socket's two lips and padding,
 * the row's own padding and two gaps, and the mastery plate's rim. A constant
 * for the same reason `GAUGE_CHROME_PX` is one - it is compared against a
 * measured width only to pick a WHOLE magnification.
 */
const SKILL_CHROME_PX = 44;

/** The gutter between the portrait and the name column, at the widest scale
 *  step the kit ships, plus the state line's own slack. */
const NAME_COLUMN_CHROME_PX = 10;

function SkillLine(props: {
  readonly content: ContentRegistry | null;
  readonly def: SkillDefId;
  readonly level: number;
  /** Magnification for both marks, picked by the stack. */
  readonly scale: number;
}): JSX.Element {
  const def = props.content?.skills.get(props.def);
  // Named `mark`, not `key`: an army socket a hundred lines down holds a
  // creature FRAME in a variable of that name, and the two are opposite cases -
  // this one is a `ui.*` medallion authored on an opaque ground and has to be
  // screened onto its socket, that one is trimmed art that must not be.
  // `packages/ui/test/adventure-sidebar.spec.ts` tells them apart by name.
  const mark = def === undefined ? null : uiIconKey(def.art);
  return (
    <div className="hm-adv-skill" title={def?.description[props.level - 1]}>
      {/* Same medallion as the treasury's: a socket with the mark screened onto
          its floor. Unblended these were two 16x16 blocks of pure black lying
          on the plank - the icon's own authored ground, not a shadow. */}
      {mark !== null && (
        <span className="hm-adv-skill__socket">
          <Icon atlasKey={mark} size={METRICS.iconSmall} className="hm-relief__inlay" />
        </span>
      )}
      {/* Both stamped. A serif skill name and a serif mastery tag sat under
          three bitmap gauges and beside a bitmap hero name; one card, one
          face. */}
      <span className="hm-adv-skill__name">
        <BitmapText face={romanFaceForStep(props.scale)} scale={1}>{def?.name ?? humanise(props.def as string)}</BitmapText>
      </span>
      <span className="hm-adv-skill__rank">
        <BitmapText face={romanFaceForStep(props.scale)} scale={1}>{skillLevelName(props.level)}</BitmapText>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------- army strip

const ARMY_SLOTS = 7;

/**
 * Why the rack is one row of seven and not the classic's four-by-two - measured.
 *
 * The classic does NOT put seven army sockets in a row on the ADVENTURE bar. This was
 * read off a reference screenshot,
 * which does not exist - but the claim re-measures, and it is worth being
 * precise about which claim.
 *
 * Checked 2026-08-30 on a reference screenshot and `classic20.jpg`,
 * genuine adventure frames with a hero selected in the sidebar. The info panel
 * lays the army out in FOUR columns and two rows: three creature sockets plus
 * the spell-point scroll on the top row, four creature sockets on the bottom,
 * with the stack count printed in white serif on the WOOD UNDER the socket
 * rather than on a plaque inside it. Measured on `classic12`, the empty bottom row
 * gives socket bevels at x = 560, 591, 623, 655, 686 in the frame's own 710px
 * width - a pitch of 31.5, or 35.5 rescaled to native 800x600 - inside a
 * sidebar that starts at native x = 601, i.e. about 199px wide. The art in
 * those sockets is a cropped head-and-shoulders creature portrait, which is why
 * a near-square socket works there and would not here.
 *
 * (The "60x63 px in a ~260px sidebar" the missing HD file was said to give is
 * NOT confirmed by this and is dropped: the numbers above are native 800x600
 * and do not scale to it in any way this file can check.)
 *
 * Worth keeping separate, because it is the same game contradicting the same
 * sentence: the classic's HERO SCREEN does put seven army sockets in one row -
 * a reference screenshot, bevels at x = 62, 121, 180, 238, 297, 356, 415, 474. It is
 * the adventure bar specifically that folds them into four by two.
 *
 * Ours draws seven across, so a socket is 32x67 at 1920x1080 and 21x51 at
 * 1366x768, and `fitSpriteBox` then draws a 149x293 battle frame at 21x41 and
 * 16x32 - a 7x and a 9x downscale of a full standing figure. That is the defect.
 *
 * It is not fixable by tuning this row. The drawn width is
 * `floor(149 / ceil(max(149/innerW, 293/innerH)))`, so the sprite grows only
 * when BOTH axes of the socket grow, which means fewer sockets per row, which
 * means two rows. Measured in this build:
 *
 *   viewport   sheet   rack now   rack 4x2   sprite now   sprite 4x2
 *   1920x1080  393px      67px      233px      21x41        37x73
 *   1366x768   284px      51px      164px      16x32        29x58
 *
 * and the sheet has NO slack to pay with: probed on the running app, the hero
 * sheet's scroll area is 302px of content in 302px at 1920x1080 and 218 in 217
 * at 1366x768. Two rows would push 166px of a day-one hero's sheet below the
 * fold at 1080p - the stat pills included.
 *
 * The classic affords it because its info bar carries nothing else: no experience
 * gauge, no movement or mana gauge, no secondary-skill list. Its army rack is
 * 58% of the panel and so would ours be. Reflowing therefore has to be decided
 * together with what leaves the panel, and that is a bigger change than a rack
 * fix; it is written up rather than half-done here.
 */
export const ARMY_ROW_NOTE = "one row of seven; see the note above before changing it";

/**
 * The margin between a creature and every wall of its socket, as a fraction of
 * the socket's width. One number for the whole rack: that is what makes seven
 * differently-shaped sprites look fitted rather than dropped in.
 */
const ARMY_INSET = 0.11;

/**
 * How many times taller than wide the sprite WELL is - the part of the socket
 * the creature is actually drawn in, above the count plaque's shelf.
 *
 * The socket used to be sized the other way round, as a fixed 2.2x multiple of
 * its own width, and that number then had to absorb the plaque, the inset and
 * the lip as well as the art. It could not: `fitSpriteBox` binds on whichever
 * axis needs the larger WHOLE divisor, the sockets are always width-bound here
 * (17px of well against a 149px frame is divisor 9), and every pixel of height
 * past `frameHeight / thatDivisor` is dead. Measured at 1366x768 before this
 * change: a 21x46 socket drew a 16x32 sprite with 10px of unusable headroom
 * above it, and the plaque then went in the corner on top of the frame because
 * there was nowhere else that was not the creature.
 *
 * So the height is derived instead: well = round(wellWidth * this), and the
 * socket is inset + well + shelf. 2.0 is the tallest aspect the creature
 * frames run at (149x293 is 1.97), which is exactly the condition for height
 * never to be the binding axis - so the drawn sprite is unchanged at both
 * viewports and the socket stops carrying height it cannot use.
 */
const ARMY_WELL_RATIO = 2.0;

/**
 * The shelf reserved at the foot of every socket for the count plaque.
 *
 * The plaque is `BitmapText` at magnification 1, so it is exactly seven glyph
 * rows plus a pixel of seat each side. It does NOT scale with `--hm-scale`,
 * because a bitmap numeral steps by whole magnifications or not at all, and
 * this one is on 1.
 *
 * It was nine rows until 2026-08-29, when the face's keyline became opt-in and
 * defaulted off: a dilated 1px keyline on all eight neighbours cost 2.4-2.6x
 * more keyline than body and closed the counters of the digits, and it is only
 * wanted on the few strings drawn over uncontrolled art. This plaque sits in a
 * shelf on the panel, not on a creature, so it takes no keyline and the shelf
 * lost the two rows that were reserving space for one.
 *
 * It exists because the plaque was drawn at `right:0; bottom:0` of the socket,
 * which is not "in the socket's corner" but "on top of the socket's frame":
 * measured on the shipped shots it replaced 14 of the 22 pixels of the lit
 * bottom lip at 1366x768 and 14 of 33 at 1920x1080, and lay across both
 * creatures' shins. A readout may sit in a panel; it may not delete the
 * panel's edge.
 */
const COUNT_SHELF_PX = 9;

/**
 * The shelf as DRAWN: the plaque's count is a bitmap reading held to the
 * window's cap floor, 11 rows on a laptop window and 15 on a 1080p one,
 * and a 9px shelf under a 15-row numeral clipped six rows of it. The shelf
 * is never under `COUNT_SHELF_PX`, and is otherwise the line box of a digit
 * at magnification 1 under the ratio the page is at.
 */
function countShelfPx(devicePixelRatio: number): number {
  return Math.max(COUNT_SHELF_PX, Math.ceil(measureDrawnBitmapText("0", 1, "roman", devicePixelRatio).height));
}

/**
 * The socket's own lit lip, in CSS px, and therefore the least the plaque may
 * be offset from the socket's edge.
 *
 * `.hm-adv-army` is `reliefDecls("recess", "wood", 3)` and `relief.ts` draws a
 * depth-N edge as N px - not scaled, at either viewport. The plaque is a
 * depth-1 chamfer and a chamfer casts `0 1px`, so it needs the lip's 3px plus
 * its own cast to sit wholly on the socket's floor: 4.
 *
 * The sprite inset is 11% of the socket's width, which is 2px at 1366x768 and
 * so narrower than the lip on its own. Verified by dumping the lip's three
 * rows under the plaques on the re-shot laptop PNG: at offset 2 all three read
 * broken, at 3 the top row still took the cast shadow, at 4 all three run
 * unbroken across all seven sockets - which is what 1920x1080 already had,
 * because there the 11% inset happens to come out at 4.
 */
const ARMY_LIP_PX = 4;

export function ArmyStrip(props: {
  readonly army: ArmyState | null;
  readonly content: ContentRegistry | null;
  readonly slotSize?: number | undefined;
  /** Measured width the seven slots have to share, in CSS pixels. */
  readonly width?: number | undefined;
}): JSX.Element {
  // The seven slots share whatever the panel is wide. The gutter is computed
  // here rather than left to the stylesheet because the slot width is derived
  // from it: if the two disagree the art is fitted to a socket that is not the
  // size it is drawn at, and the fit is exactly what this rack got wrong.
  const width = props.width ?? 0;
  const gap = width <= 0 ? 3 : Math.max(2, Math.round(width / 80));
  const measured = width <= 0
    ? METRICS.portraitSmall
    : Math.floor((width - (ARMY_SLOTS - 1) * gap) / ARMY_SLOTS);
  const size = props.slotSize ?? Math.max(18, Math.min(56, measured));
  const inset = Math.max(2, Math.round(size * ARMY_INSET));
  // The plaque's offset from the socket's edge has to clear the socket's own
  // lip, which is a flat 3px whatever the scale is.
  const lip = Math.max(inset, ARMY_LIP_PX);
  // Height is DERIVED, not multiplied out of the width: a well the sprite can
  // fill, plus the shelf the count sits on, plus one inset at the top. Nothing
  // in the socket is height the fit cannot use.
  const wellH = Math.max(8, Math.round((size - inset * 2) * ARMY_WELL_RATIO));
  const shelf = countShelfPx(useDevicePixelRatio());
  const height = inset + wellH + lip + shelf;
  const slots = useMemo<readonly (ArmySlot | null)[]>(() => {
    const source = props.army?.slots ?? [];
    const out: (ArmySlot | null)[] = [];
    for (let i = 0; i < ARMY_SLOTS; i++) out.push(source[i] ?? null);
    return out;
  }, [props.army]);

  return (
    // `hm-adv-armyrow` is the one thing on this panel that does not give: it is
    // a flex item with a pixel height in its children, so the default
    // `flex-shrink: 1` let the sheet's own content squeeze the row's box under
    // its contents and the panel's `overflow: hidden` then sheared the sockets'
    // lower lip and the count plaques' bottom row off.
    <div className="hm-row hm-adv-armyrow" style={{ gap }}>
      {slots.map((slot, i) => (
        <CreatureSlot
          key={i}
          slot={slot}
          content={props.content}
          height={height}
          inset={inset}
          lip={lip}
          shelf={shelf}
          innerW={Math.max(4, size - inset * 2)}
          innerH={wellH}
        />
      ))}
    </div>
  );
}

/**
 * One army slot: a recess with a creature standing on its floor.
 *
 * The socket's material is in `material.ts`; what is decided here is the fit.
 * The art is fitted to the socket's own rectangle by `fitSpriteBox` (via
 * `Icon`'s `boxHeight`), so every stack keeps the same margin from the same
 * walls whatever its frame's aspect ratio, and it is seated on
 * the floor rather than centred in the void, so all seven share one ground
 * line - which is how a rank of figures reads as an army instead of as seven
 * cut-outs at seven heights.
 */
function CreatureSlot(props: {
  readonly slot: ArmySlot | null;
  readonly content: ContentRegistry | null;
  readonly height: number;
  readonly inset: number;
  /** The socket's lit lip, and so the plaque's offset from its edges. */
  readonly lip: number;
  /** The shelf the count sits on, in CSS px - see `countShelfPx`. */
  readonly shelf: number;
  readonly innerW: number;
  readonly innerH: number;
}): JSX.Element {
  const slot = props.slot;
  const def = slot === null ? undefined : props.content?.creatures.get(slot.def as CreatureDefId);
  const key = def === undefined ? null : creatureFrameKey(def.art, 0);
  const label = def?.namePlural ?? (slot === null ? "Empty" : humanise(slot.def as string));

  return (
    <Slot
      empty={slot === null}
      title={slot === null ? "Empty" : `${formatNumber(slot.count)} ${label}`}
      className="hm-adv-army"
      style={{
        flex: "1 1 0",
        minWidth: 0,
        height: props.height,
        // The creature's floor is the TOP of the plaque's shelf, so the sprite
        // stands on the shelf rather than in it and the count sits beside its
        // feet instead of across its shins.
        paddingBottom: props.lip + props.shelf,
        // Read by `.hm-adv-count` in material.ts, so the plaque and the shelf
        // that was reserved for it can never disagree about their size.
        "--hm-adv-shelf": `${String(props.shelf)}px`,
        "--hm-adv-lip": `${String(props.lip)}px`,
      } as CSSProperties}
    >
      {key !== null && (
        <Icon
          atlasKey={key}
          size={props.innerW}
          boxHeight={props.innerH}
          className="hm-adv-army__art"
        />
      )}
      {/* A count stamped on the socket's own plate, in the same bitmap face the
          battlefield's plaques use. It used to be a 12px vector numeral, which
          on a 46-colour creature sprite is the loudest thing in the panel. */}
      {slot !== null && (
        <span className="hm-adv-count">
          <BitmapText face="roman" scale={1}>{formatCount(slot.count)}</BitmapText>
        </span>
      )}
    </Slot>
  );
}
